// public/js/features/display/display.css.js
// Styles for the display-template editor, injected on demand.
//
// WHY here and not app.css: the template editor is a self-contained, rarely
// opened feature. Shipping its ~150 lines in the global stylesheet would grow
// the first paint for everyone and add another shared file every parallel change
// has to touch. The rules are namespaced `dt-` (display templates) and injected
// once, the first time an editor or the settings tab renders.
//
// Colours come from the app's design tokens (--brand-primary, --ink, --line from
// app.css) and Bootstrap's --bs-* variables, so the editor follows the app theme
// instead of hard-coding a second palette.

const STYLE_ID = 'display-templates-styles';

export const DISPLAY_CSS = `
/* ── settings tab: one card per surface ─────────────────────────────────── */
.dt-surface-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.dt-surface-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line, #e5e7eb);
  border-radius: 12px;
  background: var(--bs-body-bg, #fff);
  padding: 16px;
  transition: box-shadow .15s ease, border-color .15s ease;
}
.dt-surface-card:hover {
  border-color: var(--brand-primary, #2563eb);
  box-shadow: 0 4px 14px rgba(15, 23, 42, .07);
}
.dt-surface-head { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.dt-surface-icon {
  width: 34px; height: 34px; flex: 0 0 34px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 9px;
  background: rgba(37, 99, 235, .10);
  color: var(--brand-primary, #2563eb);
}
.dt-surface-title { font-weight: 600; font-size: .95rem; line-height: 1.2; }
.dt-surface-desc { color: var(--ink-soft, #475569); font-size: .82rem; flex: 1 1 auto; margin-bottom: 12px; }
.dt-surface-foot { display: flex; align-items: center; justify-content: space-between; gap: 8px; }

/* ── reorderable rows (columns / detail fields / card blocks) ───────────── */
.dt-list { list-style: none; margin: 0; padding: 0; }
.dt-row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px;
  border: 1px solid var(--line, #e5e7eb);
  border-radius: 9px;
  background: var(--bs-body-bg, #fff);
  margin-bottom: 6px;
  /* Fixed height keeps the list from jumping while rows are dragged/moved. */
  min-height: 46px;
}
.dt-row.dt-dragging { opacity: .45; }
.dt-row.dt-drop-before { box-shadow: inset 0 3px 0 -1px var(--brand-primary, #2563eb); }
.dt-row.dt-drop-after  { box-shadow: inset 0 -3px 0 -1px var(--brand-primary, #2563eb); }
.dt-row.dt-hidden-row { background: var(--bs-secondary-bg, #f8f9fa); }
.dt-row.dt-hidden-row .dt-row-label { color: var(--ink-soft, #475569); }
.dt-grip { cursor: grab; color: #94a3b8; padding: 2px 2px; touch-action: none; }
.dt-grip:active { cursor: grabbing; }
.dt-row-label { font-size: .88rem; font-weight: 500; }
.dt-row-key { font-size: .7rem; color: #94a3b8; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.dt-row-main { flex: 1 1 auto; min-width: 0; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.dt-row-actions { display: flex; gap: 4px; flex: 0 0 auto; }
.dt-hint { font-size: .78rem; color: var(--ink-soft, #475569); }

/* ── block editor ───────────────────────────────────────────────────────── */
.dt-block { display: block; padding: 10px; }
.dt-block-head { display: flex; align-items: center; gap: 10px; }
.dt-block-title { font-size: .88rem; font-weight: 600; flex: 1 1 auto; }
.dt-block-body { margin-top: 8px; }
.dt-block textarea {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: .82rem;
  line-height: 1.5;
  resize: vertical;
}

/* ── variable palette ───────────────────────────────────────────────────── */
.dt-palette {
  border: 1px solid var(--line, #e5e7eb);
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--bs-secondary-bg, #f8f9fa);
}
.dt-palette-groups { max-height: 190px; overflow-y: auto; }
.dt-palette-group-title {
  font-size: .68rem; text-transform: uppercase; letter-spacing: .04em;
  color: var(--ink-soft, #475569); margin: 8px 0 4px;
}
.dt-var {
  border: 1px solid var(--line, #e5e7eb);
  background: var(--bs-body-bg, #fff);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: .76rem;
  margin: 0 4px 4px 0;
  cursor: pointer;
  white-space: nowrap;
  transition: background .12s ease, border-color .12s ease, color .12s ease;
}
.dt-var:hover, .dt-var:focus-visible {
  border-color: var(--brand-primary, #2563eb);
  background: rgba(37, 99, 235, .08);
  color: var(--brand-primary, #2563eb);
  outline: none;
}

/* ── live preview ───────────────────────────────────────────────────────── */
.dt-preview-pane { position: sticky; top: 0; }
.dt-preview-label {
  font-size: .68rem; text-transform: uppercase; letter-spacing: .04em;
  color: var(--ink-soft, #475569); margin-bottom: 6px;
}
.dt-preview-stage {
  border: 1px dashed var(--line, #e5e7eb);
  border-radius: 12px;
  padding: 18px 14px;
  background:
    linear-gradient(45deg, rgba(148,163,184,.10) 25%, transparent 25%, transparent 75%, rgba(148,163,184,.10) 75%),
    linear-gradient(45deg, rgba(148,163,184,.10) 25%, transparent 25%, transparent 75%, rgba(148,163,184,.10) 75%);
  background-size: 16px 16px;
  background-position: 0 0, 8px 8px;
  display: flex; justify-content: center;
}
.dt-preview-card {
  width: 100%; max-width: 300px;
  background: var(--bs-body-bg, #fff);
  border-radius: 10px;
  box-shadow: 0 6px 22px rgba(15, 23, 42, .16);
  overflow: hidden;
  font-size: .82rem;
}
.dt-preview-card-head {
  background: #0f172a; color: #fff;
  padding: 8px 12px; font-weight: 600; font-size: .84rem;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.dt-preview-card-sub {
  padding: 8px 12px 0;
  font-weight: 500; font-size: .82rem; line-height: 1.35;
  color: var(--bs-body-color, #1e293b);
  overflow-wrap: anywhere;
}
.dt-preview-card-body { padding: 10px 12px; }
.dt-preview-text { line-height: 1.5; }
.dt-preview-photo {
  height: 84px; border-radius: 8px;
  background: var(--bs-secondary-bg, #f1f5f9);
  color: #94a3b8;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 8px;
}
.dt-preview-empty { color: #94a3b8; font-style: italic; }

/* map pin preview */
.dt-pin { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 6px 0; }
.dt-pin-dot {
  width: 16px; height: 16px; border-radius: 50%;
  background: #22c55e; border: 3px solid #2563eb;
  box-shadow: 0 1px 4px rgba(15, 23, 42, .35);
}
.dt-pin-label {
  background: rgba(255, 255, 255, .92);
  border: 1px solid var(--line, #e5e7eb);
  border-radius: 6px;
  padding: 1px 6px;
  font-size: .72rem; font-weight: 600;
  color: var(--ink, #0f172a);
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .18);
}

/* ── modal footer: reset on the left, saves on the right ────────────────── */
.dt-footer { display: flex; align-items: center; gap: 8px; width: 100%; flex-wrap: wrap; }
.dt-footer .dt-footer-spacer { flex: 1 1 auto; }

@media (max-width: 575.98px) {
  .dt-row { flex-wrap: wrap; }
  .dt-preview-pane { position: static; margin-top: 14px; }
  .dt-footer > .btn { flex: 1 1 auto; }
}
`;

let _injected = false;

/** Inject the editor stylesheet once (idempotent, safe to call on every render). */
export function injectDisplayStyles() {
  if (_injected || document.getElementById(STYLE_ID)) { _injected = true; return; }
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = DISPLAY_CSS;
  document.head.appendChild(style);
  _injected = true;
}
